import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, ClipboardList, Gauge, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { OUTCOME_TIERS, TONE_STYLES } from "@/lib/sim";
import { computeCaseScore } from "@/lib/progression";
import { fetchAllSessions, facilitatorStatus, fetchKnowledgeChecks, fetchFeedback, downloadExportZip } from "@/lib/api";
import { TraineeDrilldown } from "@/components/sim/TraineeDrilldown";
import { FacilitatorGate } from "@/components/sim/FacilitatorGate";

const TIER_BADGE = {
  good: "bg-emerald-100 text-emerald-800",
  caution: "bg-amber-100 text-amber-800",
  harmful: "bg-rose-100 text-rose-800",
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-stone-200 bg-white px-5 py-4">
    <div className="flex items-center gap-2 text-stone-400">
      <Icon className="h-4 w-4" />
      <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <div className="mt-1.5 font-heading text-2xl font-semibold text-stone-900 tabular-nums">{value}</div>
  </div>
);

const TOKEN_KEY = "sps_facilitator_token";

function deltaBadgeClass(delta) {
  if (delta > 0) return "bg-emerald-100 text-emerald-800";
  if (delta < 0) return "bg-rose-100 text-rose-800";
  return "bg-stone-100 text-stone-600";
}

export default function FacilitatorDashboard() {
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState(false);
  const [trainee, setTrainee] = useState("all");
  const [caseFilter, setCaseFilter] = useState("all");
  const [cohortFilter, setCohortFilter] = useState("all");
  const [configured, setConfigured] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [checks, setChecks] = useState([]);
  const [feedback, setFeedback] = useState([]);

  useEffect(() => {
    facilitatorStatus().then(setConfigured).catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchAllSessions(token)
      .then(setSessions)
      .catch((e) => {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          setError(true);
        }
      });
    fetchKnowledgeChecks(token).then(setChecks).catch(() => {});
    fetchFeedback(token).then(setFeedback).catch(() => {});
  }, [token]);

  const handleUnlocked = (t) => {
    sessionStorage.setItem(TOKEN_KEY, t);
    setConfigured(true);
    setToken(t);
  };

  const scored = useMemo(
    () => (sessions || []).map((s) => ({ ...s, score: computeCaseScore(s.final_meters, s.choices) })),
    [sessions]
  );

  const rows = useMemo(() => {
    return scored
      .filter((s) => (trainee === "all" ? true : (s.trainee_name || "Unnamed") === trainee))
      .filter((s) => (caseFilter === "all" ? true : s.scenario_title === caseFilter))
      .filter((s) => (cohortFilter === "all" ? true : (s.cohort || "") === cohortFilter));
  }, [scored, trainee, caseFilter, cohortFilter]);

  const trainees = useMemo(
    () => [...new Set((sessions || []).map((s) => s.trainee_name || "Unnamed"))].sort(),
    [sessions]
  );
  const cases = useMemo(
    () => [...new Set((sessions || []).map((s) => s.scenario_title).filter(Boolean))].sort(),
    [sessions]
  );
  const cohorts = useMemo(
    () => [...new Set((sessions || []).map((s) => s.cohort).filter(Boolean))].sort(),
    [sessions]
  );
  const avgScore = rows.length ? Math.round(rows.reduce((a, r) => a + r.score, 0) / rows.length) : 0;

  const knowledgeByTrainee = useMemo(() => {
    const map = {};
    checks.forEach((c) => {
      const name = c.trainee_name || "Unnamed";
      const m = (map[name] ||= {});
      if (!m[c.phase]) m[c.phase] = c;
    });
    return map;
  }, [checks]);

  if (error) {
    return <div className="min-h-screen grid place-items-center text-stone-500">Could not load sessions. Please refresh.</div>;
  }
  if (configured === null) {
    return <div className="min-h-screen grid place-items-center text-stone-400">Loading…</div>;
  }
  if (!token) {
    return <FacilitatorGate configured={configured} onUnlocked={handleUnlocked} />;
  }
  if (!sessions) {
    return <div className="min-h-screen grid place-items-center text-stone-400">Loading dashboard…</div>;
  }

  return (
    <div data-testid="facilitator-dashboard" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Link to="/" data-testid="dashboard-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Back to simulator
      </Link>
      <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">Facilitator view</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">Training sessions</h1>
      <p className="mt-2 text-stone-600">Every completed run, with scores and links to full reports.</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={ClipboardList} label="Sessions" value={rows.length} testId="stat-sessions" />
        <Stat icon={Users} label="Trainees" value={trainees.length} testId="stat-trainees" />
        <Stat icon={Gauge} label="Average score" value={`${avgScore}%`} testId="stat-avg-score" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <select
          data-testid="trainee-filter"
          value={trainee}
          onChange={(e) => setTrainee(e.target.value)}
          className="rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm text-stone-700 outline-none focus:border-emerald-500"
        >
          <option value="all">All trainees</option>
          {trainees.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          data-testid="case-filter"
          value={caseFilter}
          onChange={(e) => setCaseFilter(e.target.value)}
          className="rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm text-stone-700 outline-none focus:border-emerald-500"
        >
          <option value="all">All cases</option>
          {cases.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {cohorts.length > 0 && (
          <select
            data-testid="cohort-filter"
            value={cohortFilter}
            onChange={(e) => setCohortFilter(e.target.value)}
            aria-label="Filter sessions by cohort code"
            className="rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm text-stone-700 outline-none focus:border-emerald-500"
          >
            <option value="all">All cohorts</option>
            {cohorts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <button
          data-testid="export-csv-button"
          onClick={async () => {
            try {
              const blob = await downloadExportZip(token);
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "safe-path-export.zip";
              a.click();
              URL.revokeObjectURL(url);
            } catch {
              toast.error("Export failed. Please try again.");
            }
          }}
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:border-emerald-400 hover:text-emerald-800 transition-colors duration-200"
        >
          <Download className="h-4 w-4" /> Export data (CSV)
        </button>
      </div>

      {trainee !== "all" && (
        <TraineeDrilldown
          name={trainee}
          sessions={scored.filter((s) => (s.trainee_name || "Unnamed") === trainee)}
          knowledge={knowledgeByTrainee[trainee]}
          onClose={() => setTrainee("all")}
        />
      )}

      {Object.keys(knowledgeByTrainee).length > 0 && (
        <div data-testid="knowledge-checks-section" className="mt-6 rounded-2xl border border-stone-200 bg-white overflow-x-auto">
          <div className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
            Knowledge checks (pre / post)
          </div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(knowledgeByTrainee).map(([name, k]) => {
                const delta = k.pre && k.post ? k.post.score - k.pre.score : null;
                return (
                  <tr key={name} data-testid={`knowledge-row-${name}`} className="border-t border-stone-100">
                    <td className="px-5 py-3 font-medium text-stone-800">{name}</td>
                    <td className="px-5 py-3 text-stone-600 tabular-nums">{k.pre ? `${k.pre.score}/${k.pre.total} pre` : "pre pending"}</td>
                    <td className="px-5 py-3 text-stone-600 tabular-nums">{k.post ? `${k.post.score}/${k.post.total} post` : "post pending"}</td>
                    <td className="px-5 py-3">
                      {delta != null && (
                        <span data-testid={`knowledge-delta-${name}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${deltaBadgeClass(delta)}`}>
                          {delta > 0 ? `+${delta}` : delta}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {feedback.length > 0 && (
        <div data-testid="feedback-section" className="mt-6 rounded-2xl border border-stone-200 bg-white">
          <div className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
            Trainee feedback ({feedback.length})
          </div>
          <div className="divide-y divide-stone-100">
            {feedback.map((f) => (
              <div key={f.id} data-testid={`feedback-item-${f.id}`} className="px-5 py-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-medium text-stone-800">{f.trainee_name || "Unnamed"}</span>
                  {f.cohort && <span data-testid={`feedback-cohort-${f.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{f.cohort}</span>}
                  <span className="text-stone-400 text-xs">{new Date(f.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}</span>
                  <span className="text-stone-600 tabular-nums">Realism {f.realism}/5</span>
                  <span className="text-stone-600 tabular-nums">Prepared {f.preparedness}/5</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${f.would_recommend ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                    {f.would_recommend ? "Would recommend" : "Would not recommend"}
                  </span>
                </div>
                {f.most_valuable && (
                  <p className="mt-2 text-sm text-stone-600"><span className="text-stone-400">Most valuable:</span> {f.most_valuable}</p>
                )}
                {f.change_suggestion && (
                  <p className="mt-1 text-sm text-stone-600"><span className="text-stone-400">Would change:</span> {f.change_suggestion}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              <th className="px-5 py-3">Trainee</th>
              <th className="px-5 py-3">Case</th>
              <th className="px-5 py-3">Score</th>
              <th className="px-5 py-3">Outcome</th>
              <th className="px-5 py-3">Choices</th>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-10 text-center text-stone-400" data-testid="no-sessions">
                  No sessions yet. Completed cases will appear here.
                </td>
              </tr>
            )}
            {rows.map((s) => {
              const tier = OUTCOME_TIERS[s.outcome_tier] || OUTCOME_TIERS.solid;
              return (
                <tr key={s.id} data-testid={`session-row-${s.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60 transition-colors duration-150">
                  <td className="px-5 py-3.5">
                    <button
                      data-testid={`trainee-link-${s.id}`}
                      onClick={() => setTrainee(s.trainee_name || "Unnamed")}
                      title="View this trainee's trend and principle breakdown"
                      className="font-medium text-stone-800 hover:text-emerald-800 underline decoration-stone-300 underline-offset-4 hover:decoration-emerald-500 transition-colors duration-150"
                    >
                      {s.trainee_name || "Unnamed"}
                    </button>
                  </td>
                  <td className="px-5 py-3.5 text-stone-500 text-xs">{s.cohort || ""}</td>
                  <td className="px-5 py-3.5 text-stone-600">{s.scenario_title}{s.ended_early ? " (ended early)" : ""}</td>
                  <td className="px-5 py-3.5 font-semibold tabular-nums text-stone-800">{s.score}%</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TIER_BADGE[tier.tone]}`}>{tier.title}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-2 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1" aria-label={`${s.good_count} good choices`}><span className={`h-2 w-2 rounded-full ${TONE_STYLES.good.dot}`} aria-hidden="true" />{s.good_count}</span>
                      <span className="inline-flex items-center gap-1" aria-label={`${s.caution_count} caution choices`}><span className={`h-2 w-2 rounded-full ${TONE_STYLES.caution.dot}`} aria-hidden="true" />{s.caution_count}</span>
                      <span className="inline-flex items-center gap-1" aria-label={`${s.harmful_count} harmful choices`}><span className={`h-2 w-2 rounded-full ${TONE_STYLES.harmful.dot}`} aria-hidden="true" />{s.harmful_count}</span>
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-stone-500 whitespace-nowrap">
                    {new Date(s.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                  </td>
                  <td className="px-5 py-3.5">
                    <Link
                      to={`/report/${s.id}`}
                      data-testid={`view-report-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:text-emerald-950"
                    >
                      Report <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
